import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { QuoteOrderLogModelType } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderLog'
import OrderStatusBar from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar'
import { displayDate } from '../../../../../../../../utils/globals/date'
import { Fragment } from 'react'
import { BuyInvoiceLogModelType } from '@teinor/erp/types/company/order/buy/buyInvoice/buyInvoiceLog'
import { getOrderStatusBarType } from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar/functions'
import { processedTypesInversed } from '@teinor/erp/types/company/order/general'
import OrderStatusBarDropDown from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBarDropDown'
import { BuyInvoiceStatusInversed } from '@teinor/erp/types/company/order/buy/buyInvoice'
import { faPenCircle } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'

interface BuyInvoiceStatusBarProps extends CreateInvoiceStatusBarProps {}

const BuyInvoiceStatusBar: React.FC<BuyInvoiceStatusBarProps> = ({ orderStatus, OrderLogs, ...logic }) => {
    const { statuses } = useCreateStatusBar({ orderStatus, OrderLogs, ...logic })
    
    return (
        <OrderStatusBar statuses={statuses} currentOrderStatus={orderStatus} getOrderStatusBarType={getOrderStatusBarType}/>
    )
}

export interface CreateInvoiceStatusBarProps {
    BuyOrderId?: number
    BuyDeliveryNoteId?: number
    orderStatus: number
    OrderLogs: QuoteOrderLogModelType[]
}

const useCreateStatusBar = ({ orderStatus, BuyOrderId, BuyDeliveryNoteId, OrderLogs, }: CreateInvoiceStatusBarProps) => {
    const { t } = useTwinTranslation()
    let statuses: TwinDictionary[] = []
    const dictOrderLogs: TwinDictionary = {}
    for (const log of OrderLogs) {
        if (!dictOrderLogs[log.logType]) {
            dictOrderLogs[log.logType] = [log]
        } else {
            dictOrderLogs[log.logType] = [...dictOrderLogs[log.logType], log]
        }
    }
    if (BuyDeliveryNoteId) {
        statuses.push(
            {
                id: -1,
                name: t('buyDeliveryNote', 'Albarán'),
                logType: 0,
                color: 'bg-gray-33',
                icon: faPenCircle,
                className: 'text-gray-33',
                extraClassName: 'border border-gray-33',
                renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} {...{BuyDeliveryNoteId, BuyOrderId}} /> : null
            },
            {
                id: 0,
                name: t('draft', 'Borrador'),
                logType: 1,
                color: 'bg-gray-33',
                icon: faPenCircle,
                className: 'text-gray-33',
                extraClassName: 'border border-gray-33',
                renderMessage: dictOrderLogs[1]?.length ? <RenderLogMessage logs={dictOrderLogs[1] || []} {...{BuyDeliveryNoteId, BuyOrderId}} /> : null
            }
        )
    } else if (BuyOrderId) {
        statuses.push(
            {
                id: -1,
                name: t('buyOrder', 'Pedido'),
                logType: 0,
                color: 'bg-gray-33',
                icon: faPenCircle,
                className: 'text-gray-33',
                extraClassName: 'border border-gray-33',
                renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} {...{BuyDeliveryNoteId, BuyOrderId}} /> : null
            },
            {
                id: 0,
                name: t('draft', 'Borrador'),
                logType: 1,
                color: 'bg-gray-33',
                icon: faPenCircle,
                className: 'text-gray-33',
                extraClassName: 'border border-gray-33',
                renderMessage: dictOrderLogs[1]?.length ? <RenderLogMessage logs={dictOrderLogs[1] || []} {...{BuyDeliveryNoteId, BuyOrderId}} /> : null
            }
        )
    }else {
        statuses.push({
            id: 0,
            name: t('draft', 'Borrador'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} {...{BuyDeliveryNoteId, BuyOrderId}} /> : null
        })
    }
    statuses.push(
        {
            id: 2,
            name: t('invoiced', 'Facturado'),
            logType: 3,
            color: 'bg-green-43',
            icon: faCheckCircle,
            className: 'text-green-43',
            renderMessage: dictOrderLogs[3]?.length ? <RenderLogMessage logs={dictOrderLogs[3] || []} {...{BuyDeliveryNoteId, BuyOrderId}}/> : null
        }
    )
    if (orderStatus === processedTypesInversed['canceled']) {
        statuses.push({
            id: 3,
            name: t('deniedP', 'Cancelado'),
            logType: 4,
            color: 'bg-red-BA',
            icon: faXmarkCircle,
            className: 'text-red-BA',
            renderMessage: dictOrderLogs[4]?.length ? <RenderLogMessage logs={dictOrderLogs[4] || []} {...{BuyDeliveryNoteId, BuyOrderId}}/> : null
        })
    } 
    return { statuses }
}

interface RenderLogMessageProps {
    BuyOrderId?: number
    BuyDeliveryNoteId?: number
    logs: BuyInvoiceLogModelType[]
}

const RenderLogMessage: React.FC<RenderLogMessageProps> = ({ logs, BuyDeliveryNoteId, BuyOrderId }) => {
    const { tVars } = useTwinTranslation()
    const renderMessage: JSX.Element[] = []
    for (const log of logs) {
        const extraDataParsed = JSON.parse(log.extraData || '{}')
        const options = { employeeTrigger: log.EmployeeTrigger?.fullname_short || '', date: displayDate(new Date(log.createdAt)), fullAssignedNumber: extraDataParsed.full_assigned_number || '', 'interpolation': { 'escapeValue': false }, }
        const dictOrderLogsMessages: TwinDictionary = {
            0: BuyDeliveryNoteId ? 
                tVars('createdInvoiceFromBuyDeliveryNoteLogMessage', '{{employeeTrigger}} creó la factura el {{date}} a partir del albarán {{fullAssignedNumber}}', options) : 
                BuyOrderId ?
                    tVars('createdInvoiceFromBuyLogMessage', '{{employeeTrigger}} creó la factura el {{date}} a partir del pedido {{fullAssignedNumber}}', options) 
                    :  tVars('createdInvoiceLogMessage', '{{employeeTrigger}} creó la factura el {{date}}', options),
            1: '',
            2:'',
            3: tVars('finishedBuyInvoiceLogMessage', '{{employeeTrigger}} marcó la factura como finalizada el {{date}}', options),
            4: tVars('deniedBuyInvoiceLogMessage', '{{employeeTrigger}} marcó la factura como denegada el {{date}}', options),
        }
        if (dictOrderLogsMessages[log.logType]) {
            renderMessage.push(<div className='my-5' key={log.id} > {dictOrderLogsMessages[log.logType]} </div>)
        }
    }
    if (!renderMessage.length) {
        return null
    }
    return (<Fragment>{renderMessage}</Fragment>)
}

export const BuyInvoiceStatusBarNew: React.FC<BuyInvoiceStatusBarProps> = ({ orderStatus, OrderLogs, ...logic }) => {
    const { statuses } = useCreateStatusBar({ orderStatus, OrderLogs, ...logic })
    return (
        <OrderStatusBarDropDown statuses={statuses} currentOrderStatus={orderStatus} getOrderStatusBarType={getOrderStatusBarType} isAccepted={orderStatus === BuyInvoiceStatusInversed['accepted']} />
    )
}

export default BuyInvoiceStatusBar